"use client";

import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { topNavEvent } from "@faire/retailer-visitor-shared/components/Header/Events";
import { HeaderStoreProvider } from "@faire/retailer-visitor-shared/components/Header/HeaderStoreProvider";
import { LazyMobileMenu } from "@faire/retailer-visitor-shared/components/Header/mobile/MobileMenu/index";
import { HeaderLinkButton } from "@faire/retailer-visitor-shared/components/Header/styled";
import { useHeaderState } from "@faire/retailer-visitor-shared/components/Header/useHeaderState";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useSettingNextJSRscCategoryPage } from "@faire/retailer-visitor-shared/settings/useSettingNextJSRscCategoryPage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Hamburger } from "@faire/web--source/ui/Hamburger";
import { useViewportWidth } from "@faire/web--source/ui/hooks/useViewportWidth";
import { route as categoryC1Route } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import { observer } from "mobx-react";
import React, { Suspense } from "react";

const HeaderMobileHamburgerMenuBase: React.FC = () => {
  const width = useViewportWidth((width) => width);
  const { strictLocalize } = useStrictLocalization();

  const isSSR = useIsSSR();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { prefetch: nextPrefetch } = useNextRouterContext();
  const { isShowingMenu, hideMenu, showMenu } = useHeaderState();
  const isRSCCategoryPageEnabled = useSettingNextJSRscCategoryPage();
  const retailerToken = getRetailer()?.token;
  const toggleMenu = () => {
    const trackToggleEvent = isShowingMenu
      ? topNavEvent("close", retailerToken ?? "")
      : topNavEvent("open", retailerToken ?? "");
    if (!isShowingMenu && isLoggedInRetailer && !isRSCCategoryPageEnabled) {
      nextPrefetch(categoryC1Route);
    }
    isShowingMenu ? hideMenu() : showMenu();
    trackToggleEvent();
  };

  return (
    <>
      <HeaderLinkButton
        onClick={toggleMenu}
        data-test-id="mobile-menu-button"
        disabled={isSSR}
        $width={width}
        aria-label={strictLocalize({
          defaultMessage: "Menu button",
          description: {
            text: "Aria label for the button to open navigation",
            img: "https://cdn.faire.com/fastly/067a8289eb46d43aa3276e51ef58803df8e1901bf04a10ec0ec0e4aecadd24f2.png",
          },
        })}
      >
        <Hamburger />
      </HeaderLinkButton>
      {isShowingMenu ? (
        <Suspense fallback={null}>
          <LazyMobileMenu onClose={toggleMenu} />
        </Suspense>
      ) : null}
    </>
  );
};

const HeaderMobileHamburgerMenuWrapper = () => {
  return (
    <HeaderStoreProvider>
      <HeaderMobileHamburgerMenuBase />
    </HeaderStoreProvider>
  );
};

export const HeaderMobileHamburgerMenu = observer(
  HeaderMobileHamburgerMenuWrapper
);
